import { useEffect, useId, useRef, useState } from "react";
import { usePublicPanel } from "../../api";
import { usePanelPath } from "../../routing";

const privacyHref = "/privacy";
const flexPrivacyPolicy = "https://flexwms.co.uk/privacy";
const termsHref = "/terms";

export function HelpSupportDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownId = useId();
  const publicPanel = usePublicPanel();
  const hasPrivacyPolicy = publicPanel?.data?.portalBranding?.privacyPolicy;
  const hasTerms = publicPanel?.data?.portalBranding?.termsOfUse;
  const panelUrl = usePanelPath();
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeydown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [open]);

  return (
    <div className="notify-menu" ref={rootRef}>
      <button
        aria-controls={open ? dropdownId : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Help and support"
        className={`topbar-notify topbar-help ${open ? "open" : ""}`.trim()}
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <path
            d="M7.8 7.1a2.7 2.7 0 115 1.3c0 1.8-1.9 2.4-2.7 3.4-.3.4-.4.7-.4 1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="10" cy="14.6" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="10" cy="10" r="8" />
        </svg>
      </button>

      {open && (
        <div
          className="notifications-dropdown help-dropdown"
          id={dropdownId}
          role="dialog"
          aria-label="Help and support"
        >
          <div className="notifications-hdr">
            <div>
              <div className="notifications-title title-sm">Need Help?</div>
              <div className="notifications-sub">
                Got a question? Get in touch and we'll get back to you as soon
                as we can.
              </div>
            </div>
          </div>

          <div className="notifications-body help-dropdown-body">
            <div className="help-links">
              <a
                className="help-link"
                href={
                  hasPrivacyPolicy ? panelUrl(privacyHref) : flexPrivacyPolicy
                }
                target={hasPrivacyPolicy ? "_blank" : undefined}
                onClick={() => setOpen(false)}
              >
                Privacy Policy
              </a>
              {!!hasTerms && (
                <a
                  className="help-link"
                  href={panelUrl(termsHref)}
                  onClick={() => setOpen(false)}
                >
                  Terms of Use
                </a>
              )}
              {!!publicPanel?.data?.supportEmail && (
                <a
                  className="help-link"
                  href={`mailto:${publicPanel?.data?.supportEmail}`}
                  onClick={() => setOpen(false)}
                >
                  {publicPanel?.data?.supportEmail}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AppFooter({ supportEmail }: { supportEmail: string }) {
  const path = usePanelPath();
  const publicPanel = usePublicPanel();
  const hasPrivacyPolicy = publicPanel?.data?.portalBranding?.privacyPolicy;
  const hasTerms = publicPanel?.data?.portalBranding?.termsOfUse;
  return (
    <footer className="app-footer">
      {/* <div className="app-footer-title">Need Help?</div>
      <p className="app-footer-copy">
        Got a question? Get in touch and we'll get back to you as soon as we
        can.
      </p> */}
      <div className="app-footer-links">
        <a className="btn policy-link" href={`mailto:${supportEmail}`}>
          {supportEmail}
        </a>
        <span className="app-footer-divider" aria-hidden="true">
          ·
        </span>
        <a
          className="btn policy-link"
          href={"#"}
          onClick={(event) => {
            event.preventDefault();
            window.open(
              hasPrivacyPolicy ? path(privacyHref) : flexPrivacyPolicy,
              "_blank",
            );
          }}
        >
          Privacy Policy
        </a>
        {hasTerms && (
          <>
            <span className="app-footer-divider" aria-hidden="true">
              ·
            </span>
            <a
              className="btn policy-link"
              href={"#"}
              onClick={(event) => {
                event.preventDefault();
                window.open(path("terms"), "_blank");
              }}
            >
              Terms of Use
            </a>
          </>
        )}
      </div>
    </footer>
  );
}
